import axios, { type AxiosInstance, type AxiosResponse, AxiosError } from 'axios'
import { API_CONFIG } from '../config/api-config'

export interface IAPIResponse<T> {
  isSuccessful: boolean
  remark?: string | null
  payload?: T | null
}

class ApiService {
  private api: AxiosInstance
  private getAuthStore: (() => any) | null = null
  private isRefreshing = false

  constructor() {
    this.api = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: API_CONFIG.HEADERS,
    })

    // Request interceptor
    this.api.interceptors.request.use((config) => {
      let token: string | null = null

      if (this.getAuthStore) {
        try {
          const authStore = this.getAuthStore()
          token = authStore?.token
          console.log('=== API INTERCEPTOR ===')
          console.log('getAuthStore exists:', !!this.getAuthStore)
          console.log('authStore exists:', !!authStore)
          console.log('token:', token ? token.substring(0, 30) + '...' : 'NULL/UNDEFINED')
          console.log('config.url:', config.url)
        } catch (err) {
          console.error('Error getting auth store:', err)
        }
      } else {
        console.warn('getAuthStore is NULL - setAuthStore was never called')
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })
    // Response interceptor
    // Response interceptor
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any

        if (error.response?.status === 401) {
          const url = originalRequest?.url ?? ''

          const isAuthEndpoint =
            url.includes('/auth/login') ||
            url.includes('/auth/logout') ||
            url.includes('/Auth/logout') ||
            url.includes('/auth/register') ||
            url.includes('/auth/forgot-password') ||
            url.includes('/activation')

          if (!isAuthEndpoint && !this.isRefreshing) {
            // Check if we actually have a token before deciding to logout
            // If no token, this is just a race condition - ignore it
            if (this.getAuthStore) {
              const authStore = this.getAuthStore()
              const currentToken = authStore?.token

              if (!currentToken) {
                // No token in store - just reject, don't logout
                console.warn('401 with no token in store, ignoring:', url)
                return Promise.reject(error)
              }

              // We had a token and got 401 - legitimate auth failure
              this.isRefreshing = true
              console.warn('Legitimate 401, logging out:', url)

              setTimeout(async () => {
                await authStore.logout(false)
                this.isRefreshing = false
              }, 500)
            }
          }
        }

        return Promise.reject(error)
      },
    )
  }

  setAuthStore(getAuthStore: () => any): void {
    this.getAuthStore = getAuthStore
  }

  async get<T>(url: string, params?: unknown): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, { params })
    return response.data
  }

  async post<T>(url: string, data: unknown): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data)
    return response.data
  }

  async put<T>(url: string, data: unknown): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url)
    return response.data
  }
}

export const apiService = new ApiService()
